<template>
  <div id="checkout-container">
    <v-row>
      <v-col
        cols="12"
        lg="8"
        order="last"
      >
        <checkout-review
          id="checkout-review-container"
          :heading="$t('checkoutReview.heading')"
          class="checkout-order-review"
          @proceed="proceedIfValid"
        />
        <checkout-review-buttons
          class="d-flex"
          :complete-button-text="$t('checkoutReview.reviewButton')"
          :show-loading="reviewingOrder"
          :complete-button-is-disabled="disableReviewAndCompleteButton"
          @proceed="proceedIfValid"
        />
      </v-col>
      <v-col
        cols="12"
        lg="4"
        class="checkout-delivery-forms"
      >
        <div
          v-if="physicalItemsInCart"
          class="checkout-shipping-address-section"
        >
          <h4
            class="checkout-shipping-address-title svp-text-dark svp-font-weight-medium"
          >
            {{ $t('checkoutReview.physicalAddressHeading') }}
          </h4>
          <physical-delivery-form
            :form-is-valid.sync="physicalForm.isValid"
            :validate.sync="physicalForm.validate"
          />
        </div>

        <div class="checkout-digital-address-section">
          <h4
            class="checkout-shipping-address-title svp-text-dark svp-font-weight-medium"
          >
            {{ addressHeading }}
          </h4>
          <digital-delivery-form
            :form-is-valid.sync="digitalForm.isValid"
            :validate.sync="digitalForm.validate"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  GET_PHYSICAL_CART_ITEMS,
  GET_PHYSICAL_CART_ITEMS_EXIST,
  GET_PHYSICAL_RECIPIENT,
  GET_DIGITAL_CART_ITEMS_EXIST,
  ORDER_TRACKING_PROPERTIES,
  GET_CART_ITEMS
} from '@/store/get-types'
import { FETCH_ORDER_VALIDATION } from '@/store/action-types'
import PhysicalDeliveryForm from './PhysicalDeliveryForm.vue'
import CheckoutReview from './CheckoutReview'
import CheckoutReviewButtons from './CheckoutReviewButtons'
import DigitalDeliveryForm from './DigitalDeliveryForm'
import { REVIEW_AND_COMPLETE_ROUTE } from '@/router/routes/route-names'
import {
  CHECKOUT_STARTED,
  CHECKOUT_STEP_COMPLETED,
  CHECKOUT_STEP_VIEWED
} from '@/plugins/segment/SegmentConstants'

const CHECKOUT_STEP_NUMBER = 1

export default {
  name: 'Checkout',
  components: {
    PhysicalDeliveryForm,
    CheckoutReview,
    CheckoutReviewButtons,
    DigitalDeliveryForm
  },
  data () {
    return {
      reviewingOrder: false,
      width: 0,
      digitalForm: {
        isValid: true,
        validate: false
      },
      physicalForm: {
        isValid: true,
        validate: false
      }
    }
  },
  computed: {
    ...mapGetters({
      physicalCartItems: GET_PHYSICAL_CART_ITEMS,
      physicalRecipient: GET_PHYSICAL_RECIPIENT,
      physicalItemsInCart: GET_PHYSICAL_CART_ITEMS_EXIST,
      digitalItemsInCart: GET_DIGITAL_CART_ITEMS_EXIST,
      orderTrackingProperties: ORDER_TRACKING_PROPERTIES,
      cartItems: GET_CART_ITEMS
    }),
    addressHeading () {
      return this.digitalItemsInCart
        ? this.$t('checkoutReview.digitalAddressHeading')
        : this.$t('checkoutReview.physicalConfirmationHeading')
    },
    disableReviewAndCompleteButton () {
      return this.reviewingOrder || this.cartItems.length < 1
    }
  },
  async mounted () {
    this.$segment.track(CHECKOUT_STARTED, this.orderTrackingProperties)
    this.$segment.track(CHECKOUT_STEP_VIEWED, { step: CHECKOUT_STEP_NUMBER })
    await this.fetchOrderValidationRules()
  },
  methods: {
    ...mapActions({
      fetchOrderValidationRules: FETCH_ORDER_VALIDATION
    }),
    async proceedIfValid () {
      this.reviewingOrder = true
      if (await this.validateFieldsAreIncorrect()) {
        this.reviewingOrder = false
        return
      }

      this.reviewingOrder = false
      this.reviewAndComplete()
    },
    reviewAndComplete () {
      this.$segment.track(CHECKOUT_STEP_COMPLETED, {
        step: CHECKOUT_STEP_NUMBER
      })
      this.$router.push({ name: REVIEW_AND_COMPLETE_ROUTE })
    },
    async validateFieldsAreIncorrect () {
      this.digitalForm.validate = true
      this.physicalForm.validate = true
      await this.$nextTick()
      return !this.physicalForm.isValid || !this.digitalForm.isValid
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';

.checkout-shipping-address-title {
  font-weight: 500;
  font-size: $font-size-lg;
  margin-bottom: 15px;
}

#checkout-container {
  padding-top: 72px;
}

.checkout-shipping-address-section {
  margin-bottom: 25px;
}

@media screen and (max-width: $breakpoint-lg - 1) {
  #checkout-container {
    padding-top: 25px;
  }
}

@media screen and (max-width: $breakpoint-md - 1) {
  #checkout-container {
    padding-top: 25px;
    padding-bottom: 0;
  }
}
</style>
